/**
 * Dark-mode variant of an organization's brand colour. The brand colour itself is
 * used as-is on light surfaces; on the dark (gray-900) surface a dark brand would
 * be unreadable, so it is mixed toward white only as far as needed to reach WCAG
 * AA contrast (4.5:1). Light brands come back unchanged.
 */
type Rgb = [number, number, number];

/** Tailwind gray-900, the dark-mode surface. */
const DARK_SURFACE: Rgb = [17, 24, 39];
const MIN_CONTRAST = 4.5;

function parseHex(hex: string): Rgb {
  const value = hex.replace('#', '');
  const full = value.length === 3 ? value.replace(/./g, (c) => c + c) : value;
  return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16)) as Rgb;
}

function toHex(rgb: Rgb): string {
  return `#${rgb.map((c) => Math.round(c).toString(16).padStart(2, '0')).join('')}`;
}

function luminance([r, g, b]: Rgb): number {
  const channel = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

function contrast(a: Rgb, b: Rgb): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

function mix(color: Rgb, toward: Rgb, amount: number): Rgb {
  return color.map((c, i) => c + (toward[i] - c) * amount) as Rgb;
}

function readableOn(color: Rgb, surface: Rgb, toward: Rgb): string {
  for (let amount = 0; amount <= 1; amount += 0.05) {
    const candidate = mix(color, toward, amount);
    if (contrast(candidate, surface) >= MIN_CONTRAST) return toHex(candidate);
  }
  return toHex(toward);
}

/** Brand colour for text/icons in dark mode; unchanged when already readable. */
export function brandLightColor(hex: string): string {
  return readableOn(parseHex(hex), DARK_SURFACE, [255, 255, 255]);
}
